import CTNFilterBar, { CTNFilterButton, CTNFilterDropdown, CTNFilterSearch } from '@ctn/ui/components/ctn-filter-bar';
import { type CTNFilterBarRef } from '@ctn/ui/types/ctn-filter-bar';
import { type CTNTableRef } from '@ctn/ui/types/ctn-table';
import { forwardRef, type ReactElement, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import {
	type ISubsanacionListadoPayload,
	type ISubsanacionListadoResponse,
	postSubsanaciones,
} from '@/services/tramite.service';
import useOptionsStore from '@/store/options.store';

import { PAGE_SIZE } from '@/constants/table.constants';
import { ESTADO_SUBSANACION, TIPO_SUBSANACION } from '@/constants/tramite.constants';
import { cleanObj } from '@/helpers/objects';
import { type ISubsanacionDocumento, type ISubsanacionSujeto } from '@/interfaces/model/don.interface';
import { type IPagination } from '@/interfaces/model/generic.interface';

import SubsanacionesListado from '../SubsanacionesListado';
import { defaultValues, type FilterFormData, getSchema, PAYLOAD_PAGINATION, payloadDefault } from './config';
import { getColumnsAtendidas, getColumnsPendiente } from '../SubsanacionesListado/columns';

type ActionColumn = (rowData: ISubsanacionDocumento | ISubsanacionSujeto) => void;

type SolicitudPendiente = {
	readonly idEstado: typeof ESTADO_SUBSANACION.PENDIENTE;
	readonly onViewDetail: ActionColumn;
	readonly onSubsanar: ActionColumn;
};

type SolicitudSubsanada = {
	readonly idEstado: typeof ESTADO_SUBSANACION.SUBSANADA;
	readonly onSubsanar?: never;
	readonly onViewDetail: ActionColumn;
};

type SubsanacionesFilterProps = SolicitudPendiente | SolicitudSubsanada;

export type SubsanacionesFilterRef = {
	reload: () => void;
};

const SubsanacionesFilter = forwardRef<SubsanacionesFilterRef, SubsanacionesFilterProps>(
	({ onViewDetail, idEstado, onSubsanar }, ref): ReactElement => {
		const filterBarRef = useRef<CTNFilterBarRef>(null);
		const tipoSubsanacion = useOptionsStore((state) => state.options.tramite?.tipoSubsanacion) ?? [];

		const tableRef = useRef<CTNTableRef<ISubsanacionDocumento | ISubsanacionSujeto>>(null);
		const { loading, triggerFetch } = useApiData({ loading: ['listado'] as const });
		const [dataTable, setDataTable] = useState<ISubsanacionListadoResponse>({ totalResults: 10, listado: [] });
		const [pagination, setPagination] = useState<IPagination>(PAYLOAD_PAGINATION);

		const { watch, control, reset, setValue } = useForm<FilterFormData>({
			resolver: getSchema(),
			mode: 'onChange',
			defaultValues,
		});
		const location = useLocation();
		const { navigateData, setNavigateData } = usePageContext();
		const [isReady, setIsReady] = useState(false);
		const isInitialLoad = useRef(true);

		const search = watch('search');
		const tipoBusqueda = search?.[0]?.key ?? defaultValues.tipoBusqueda;
		const idTipoSubsanacion = watch('idTipoSubsanacion');

		const formatData = ({
			tipoBusqueda,
			busqueda,
			idTipoSubsanacion,
			...data
		}: IPagination & FilterFormData): IPagination & Partial<ISubsanacionListadoPayload> => ({
			...data,
			[tipoBusqueda]: busqueda,
			listaIdsEstados: [idEstado],
			idTipoSubsanacion: idTipoSubsanacion ? Number(idTipoSubsanacion) : undefined,
		});

		const getData = async (payload: IPagination & Partial<ISubsanacionListadoPayload>) => {
			const { data } = await triggerFetch({
				fetch: postSubsanaciones,
				payload: { ...payloadDefault({ listaIdsEstados: [idEstado] }), ...cleanObj(payload) },
				name: 'listado',
			});
			if (data) {
				setDataTable(data);
			}
		};

		const onPageChange = (start: number) => {
			let newPagination = {
				start: PAGE_SIZE.OPERACIONES * (start - 1),
				limit: PAGE_SIZE.OPERACIONES,
			};

			if (start === 1) {
				newPagination = {
					start: 0,
					limit: PAGE_SIZE.OPERACIONES,
				};
			}
			getData(
				formatData({
					...newPagination,
					tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda,
					busqueda: search?.[0]?.search ?? '',
					idTipoSubsanacion,
				}),
			);
			setPagination(newPagination);
		};


		useImperativeHandle(ref, () => ({
			reload: () =>
				getData(
					formatData({
						...pagination,
						tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda,
						busqueda: search?.[0]?.search ?? '',
						idTipoSubsanacion,
					}),
				),
		}));

		const columns = useMemo(() => {
			const columnsMap = {
				[ESTADO_SUBSANACION.PENDIENTE]: getColumnsPendiente({ onSubsanar: onSubsanar as ActionColumn, onViewDetail }),
				[ESTADO_SUBSANACION.SUBSANADA]: getColumnsAtendidas({ onViewDetail }),
			};
			return columnsMap[idEstado] ?? getColumnsPendiente({ onSubsanar: onSubsanar as ActionColumn, onViewDetail });
		}, [idEstado]);

		useEffect(() => {
			if (!navigateData?.filter?.urls?.some(path => location.pathname.includes(path))) {
				const { filter, ...resto } = navigateData ?? {};
				setNavigateData(resto);
				getData(formatData({ ...pagination, tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda, busqueda: search?.[0]?.search ?? '', idTipoSubsanacion }));
				setIsReady(true);
				return;
			}

			const {data} = navigateData.filter;
			if (!data) {
				getData(formatData({ ...pagination, tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda, busqueda: search?.[0]?.search ?? '', idTipoSubsanacion }));
				setIsReady(true);
				return;
			}
			reset({
				...defaultValues,
				search: [{ key: data.tipoBusqueda, search: data.numeroTramite }],
				tipoBusqueda: data.tipoBusqueda,
				busqueda: data.numeroTramite,
				idTipoSubsanacion: data.idTipoSubsanacion,
			});
			filterBarRef.current?.addFilters({
				search: data.numeroTramite ? [{ key: data.tipoBusqueda ?? defaultValues.tipoBusqueda, search: data.numeroTramite ?? '' }] : [],
				idTipoSubsanacion: data.idTipoSubsanacion ? String(data.idTipoSubsanacion) : undefined,
			});
			const { filter, ...resto } = navigateData ?? {};
			setNavigateData(resto);
			setIsReady(true);
			isInitialLoad.current = false;
		}, []); 

		useEffect(() => {		
			if (!isReady) return;
			if (isInitialLoad.current) {
				isInitialLoad.current = false;
				return;
			}
			const payload = formatData({
				...pagination,
				tipoBusqueda: search?.[0]?.key ?? '',
				busqueda: search?.[0]?.search ?? '',
				idTipoSubsanacion,
			});
			
			getData(payload);
			setNavigateData({ filter: { urls: [location.pathname], data: payload }});
		}, [search, tipoBusqueda, idTipoSubsanacion, isReady]);

		return (
			<section className="u-mt-24">
				<CTNFilterBar
					ref={filterBarRef}
					tagBar
					show
					className="c-pnp-filter-bar"
					control={control as any}
					setValue={setValue as any}
					reset={reset as any}
				>
					<CTNFilterSearch
						name="search" placeholder="Nº solicitud" label="Buscar"
						tag="Búsqueda"
						options={[{ label: 'Nº solicitud', value: 'numeroTramite' }]} />
					<CTNFilterDropdown
						showArrow={false}
						name="idTipoSubsanacion"
						valueFormatter={() => 'Tipo'}
						label="Tipo"
						options={tipoSubsanacion?.filter(
							({ value }) => value !== String(TIPO_SUBSANACION.NUEVO_DOCUMENTO),
						)}
						placeholder="Tipo"
						tag="Tipo"
					/>
					<CTNFilterButton
						name="clearAll"
						label="Reiniciar filtros"
						tag="Limpiar"
						icon={[{ icon: 'restartCircle', position: 'left', color: 'blue-60' }]}
						onClick={() => filterBarRef.current?.resetFilter()}
					/>
				</CTNFilterBar>
				<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 ]">
					<div className="c-pnp-box__body">
						<SubsanacionesListado
							tableRef={tableRef}
							isLoading={loading.listado}
							columns={columns}
							data={dataTable.listado}
							totalData={dataTable.totalResults}
							onPageChange={onPageChange}
						/>
					</div>
				</div>
			</section>
		);
	},
);

SubsanacionesFilter.displayName = 'SubsanacionesFilter';

export default SubsanacionesFilter;
